import { addComponent } from "bitecs";
import { ASSIST_WINDOW, TICK_RATE, Team } from "../constants.ts";
import type { ConstructKind, SpawnConstructOpts } from "../heroes/module.ts";
import { ULT_GAIN } from "../heroes/types.ts";
import { CONSTRUCTS } from "../heroes/roster/index.ts";
import { add, clamp, dist, norm, scale, type Vec3 } from "../math.ts";
import { EntityType, decodeRecord, type WorldRecords } from "../net/snapshot.ts";
import type { Obstacle } from "./physics.ts";
import type { Sim } from "./sim.ts";

export const CONSTRUCT_FLAG_SOLID = 1;
export const CONSTRUCT_FLAG_BLOCKS = 2;
export const CONSTRUCT_FLAG_DAMAGEABLE = 4;
export const CONSTRUCT_FLAG_HEALS = 8;
export const CONSTRUCT_FLAG_PERSIST = 16;
export const DEFAULT_SOLID_AFTER = 0.25;

export interface ConstructHit {
  t: number;
  eid: number;
  pos: Vec3;
  normal?: Vec3;
}

const q = (v: number, min: number, max: number, bits: number) => min + (Math.round(clamp((v - min) / (max - min), 0, 1) * ((1 << bits) - 1)) / ((1 << bits) - 1)) * (max - min);
/** Must match the snapshot's POS_XZ / POS_Y quantization exactly. */
const qpos = (p: Vec3): Vec3 => ({ x: q(p.x, -256, 256, 20), y: q(p.y, -50, 50, 17), z: q(p.z, -256, 256, 20) });
const f32 = Math.fround;
const qyaw = (yaw: number) => {
  const n = 1 << 10;
  const i = Math.round(((((yaw / (Math.PI * 2)) % 1) + 1) % 1) * (n - 1)) % n;
  let a = (i / (n - 1)) * Math.PI * 2;
  if (a > Math.PI) a -= Math.PI * 2;
  return a;
};

function flagsFor(kind: ConstructKind, opts?: SpawnConstructOpts): number {
  let flags = 0;
  if (kind.solid) flags |= CONSTRUCT_FLAG_SOLID;
  if (kind.blocksShots) flags |= CONSTRUCT_FLAG_BLOCKS;
  if (kind.damageable !== false) flags |= CONSTRUCT_FLAG_DAMAGEABLE;
  if (kind.heals) flags |= CONSTRUCT_FLAG_HEALS;
  if (opts?.ownerDeath === "persist" || (opts?.ownerDeath === undefined && kind.persistOnOwnerDeath)) flags |= CONSTRUCT_FLAG_PERSIST;
  return flags;
}

const solidAfterTicks = (kind: ConstructKind) => Math.round((kind.solidAfter ?? (kind.solid ? DEFAULT_SOLID_AFTER : 0)) * TICK_RATE);
export const constructMaterialize = (kind: ConstructKind, ageTicks: number) => {
  const t = solidAfterTicks(kind);
  return t <= 0 ? 1 : clamp(ageTicks / t, 0, 1);
};

/** Spawns a server-authoritative construct with pose quantized exactly like snapshots. */
export function spawnConstruct(sim: Sim, owner: number, kind: number, pos: Vec3, yaw: number, opts: SpawnConstructOpts = {}): number | undefined {
  if (!sim.authority) return undefined;
  const spec = CONSTRUCTS[kind];
  if (!spec || !sim.players.has(owner)) return undefined;
  const p = qpos(pos);
  const e = sim.createEntity();
  addComponent(sim.world, e, sim.c.IsConstruct);
  sim.c.Pos.x[e] = p.x;
  sim.c.Pos.y[e] = p.y;
  sim.c.Pos.z[e] = p.z;
  sim.c.Construct.kind[e] = kind;
  sim.c.Construct.owner[e] = owner;
  sim.c.Construct.team[e] = sim.c.Player.team[owner]!;
  sim.c.Construct.hp[e] = Math.min(opts.hp ?? spec.maxHp, spec.maxHp);
  sim.c.Construct.maxHp[e] = spec.maxHp;
  sim.c.Construct.ticks[e] = Math.round((opts.seconds ?? spec.seconds) * 60);
  sim.c.Construct.age[e] = 0;
  sim.c.Construct.yaw[e] = qyaw(yaw);
  sim.c.Construct.v0[e] = opts.v0 ?? 0;
  sim.c.Construct.flags[e] = flagsFor(spec, opts);
  sim.refreshObstacles();
  return e;
}

/** Destroys a construct and runs its optional hook. */
export function destroyConstruct(sim: Sim, e: number, reason: "expired" | "killed" | "owner" | "manual" = "manual") {
  const kind = CONSTRUCTS[sim.c.Construct.kind[e]!];
  kind?.onDestroy?.(sim, e, reason);
  sim.destroyEntity(e);
  sim.refreshObstacles();
}

/** Damages a damageable construct, crediting barrier-style ult charge and mitigation. */
export function damageConstruct(sim: Sim, e: number, amount: number, attacker?: number): number {
  if (!sim.authority || !canDamageConstruct(sim, e, attacker)) return 0;
  const before = sim.c.Construct.hp[e]!;
  const dealt = Math.min(before, Math.max(0, amount));
  if (dealt <= 0) return 0;
  sim.c.Construct.hp[e] = before - dealt;
  const owner = sim.c.Construct.owner[e]!;
  const meta = sim.players.get(owner);
  if (meta) meta.stats.mitigated += dealt;
  addConstructUlt(sim, attacker, dealt * 0.5);
  CONSTRUCTS[sim.c.Construct.kind[e]!]?.onDamage?.(sim, e, dealt, attacker);
  if (sim.c.Construct.hp[e]! <= 0.001) destroyConstruct(sim, e, "killed");
  return dealt;
}

/** Returns constructs within radius, sorted nearest first. */
export function constructsInRadius(sim: Sim, pos: Vec3, radius: number, filter: (e: number) => boolean = () => true): { eid: number; d: number }[] {
  const out: { eid: number; d: number }[] = [];
  for (const e of sim.constructEids()) {
    if (!filter(e)) continue;
    const d = constructSurfaceDist(sim, e, pos);
    if (d <= radius) out.push({ eid: e, d });
  }
  return out.sort((a, b) => a.d - b.d);
}

/** Enemy constructs owned by the opposing team. */
export const enemyConstructs = (sim: Sim, owner: number) => sim.constructEids().filter((e) => sim.c.Construct.team[e] !== sim.c.Player.team[owner]);

export function canDamageConstruct(sim: Sim, e: number, attacker?: number): boolean {
  if (!(sim.c.Construct.flags[e]! & CONSTRUCT_FLAG_DAMAGEABLE)) return false;
  if (attacker === undefined || sim.c.Construct.team[e] !== sim.c.Player.team[attacker]) return true;
  return !!CONSTRUCTS[sim.c.Construct.kind[e]!]?.friendlyDamage;
}

export function canTargetConstruct(sim: Sim, e: number, shooter: number): boolean {
  const flags = sim.c.Construct.flags[e]!;
  if (sim.c.Construct.team[e] !== sim.c.Player.team[shooter]) return !!(flags & (CONSTRUCT_FLAG_BLOCKS | CONSTRUCT_FLAG_DAMAGEABLE));
  return !!(flags & CONSTRUCT_FLAG_DAMAGEABLE) && !!CONSTRUCTS[sim.c.Construct.kind[e]!]?.friendlyDamage;
}

/** Enemy players and enemy damageable constructs in radius; useful for summon target AI. */
export function nearestEnemy(sim: Sim, owner: number, from: Vec3, range: number, opts: { los?: boolean } = {}): { eid: number; construct: boolean; d: number } | undefined {
  let best: { eid: number; construct: boolean; d: number } | undefined;
  const consider = (eid: number, construct: boolean, p: Vec3, r = 0) => {
    const d = Math.max(0, dist(from, p) - r);
    if (d > range || (best && d >= best.d)) return;
    if (opts.los && !sim.physics.los(from, construct ? p : add(p, { x: 0, y: sim.hero(eid).height * 0.5, z: 0 }))) return;
    best = { eid, construct, d };
  };
  for (const e of sim.playerEids()) if (e !== owner && sim.c.Player.alive[e] && sim.c.Player.team[e] !== sim.c.Player.team[owner]) consider(e, false, sim.posOf(e), sim.hero(e).radius);
  for (const e of enemyConstructs(sim, owner)) {
    if (!(sim.c.Construct.flags[e]! & CONSTRUCT_FLAG_DAMAGEABLE)) continue;
    const shape = CONSTRUCTS[sim.c.Construct.kind[e]!]?.shape;
    consider(e, true, add(sim.posOf(e), { x: 0, y: (shape?.h ?? 1) * 0.5, z: 0 }), constructRadius(sim, e));
  }
  return best;
}

export function constructRadius(sim: Sim, e: number): number {
  const s = CONSTRUCTS[sim.c.Construct.kind[e]!]?.shape;
  return !s ? 0.5 : s.kind === "cyl" ? s.r : Math.sqrt(s.hw * s.hw + s.hd * s.hd);
}

/** Distance from `pos` to the nearest point of a construct's shape (0 inside it). */
export function constructSurfaceDist(sim: Sim, e: number, pos: Vec3): number {
  const shape = CONSTRUCTS[sim.c.Construct.kind[e]!]?.shape;
  const p = sim.posOf(e);
  if (!shape) return Math.max(0, dist(pos, p) - 0.5);
  const rx = pos.x - p.x;
  const rz = pos.z - p.z;
  const dy = Math.max(p.y - pos.y, 0, pos.y - (p.y + shape.h));
  if (shape.kind === "cyl") return Math.hypot(Math.max(0, Math.hypot(rx, rz) - shape.r), dy);
  const yaw = sim.c.Construct.yaw[e]!;
  const lx = rx * Math.cos(yaw) - rz * Math.sin(yaw);
  const lz = rx * Math.sin(yaw) + rz * Math.cos(yaw);
  return Math.hypot(Math.max(0, Math.abs(lx) - shape.hw), Math.max(0, Math.abs(lz) - shape.hd), dy);
}

function rayBox(origin: Vec3, dir: Vec3, center: Vec3, yaw: number, hw: number, hd: number, h: number, maxDist: number, grow: number): { t: number; normal: Vec3 } | null {
  const sy = Math.sin(yaw);
  const cy = Math.cos(yaw);
  const rx = origin.x - center.x;
  const rz = origin.z - center.z;
  const slabs = [
    [rx * cy - rz * sy, dir.x * cy - dir.z * sy, hw + grow, { x: cy, y: 0, z: -sy }],
    [rx * sy + rz * cy, dir.x * sy + dir.z * cy, hd + grow, { x: sy, y: 0, z: cy }],
    [origin.y - (center.y + h / 2), dir.y, h / 2 + grow, { x: 0, y: 1, z: 0 }],
  ] as const;
  let tmin = 0;
  let tmax = maxDist;
  let normal = { x: 0, y: 0, z: 0 };
  for (const [p, d, e, n] of slabs) {
    if (Math.abs(d) < 1e-12) {
      if (p < -e || p > e) return null;
      continue;
    }
    let t1 = (-e - p) / d;
    let t2 = (e - p) / d;
    let side = -1;
    if (t1 > t2) {
      const t = t1;
      t1 = t2;
      t2 = t;
      side = 1;
    }
    if (t1 > tmin) {
      tmin = t1;
      normal = scale(n, side);
    }
    if (t2 < tmax) tmax = t2;
    if (tmin > tmax) return null;
  }
  return { t: tmin, normal };
}

function rayCyl(origin: Vec3, dir: Vec3, center: Vec3, r0: number, h0: number, maxDist: number, grow: number): { t: number; normal: Vec3 } | null {
  let best: { t: number; normal: Vec3 } | null = null;
  const r = r0 + grow;
  const h = h0 + grow * 2;
  center = { x: center.x, y: center.y - grow, z: center.z };
  const ox = origin.x - center.x;
  const oz = origin.z - center.z;
  const a = dir.x * dir.x + dir.z * dir.z;
  const b = 2 * (ox * dir.x + oz * dir.z);
  const c = ox * ox + oz * oz - r * r;
  if (a > 1e-12) {
    const disc = b * b - 4 * a * c;
    if (disc >= 0) {
      const t = (-b - Math.sqrt(disc)) / (2 * a);
      const y = origin.y + dir.y * t;
      if (t >= 0 && t <= maxDist && y >= center.y && y <= center.y + h) best = { t, normal: norm({ x: ox + dir.x * t, y: 0, z: oz + dir.z * t }) };
    }
  }
  if (Math.abs(dir.y) > 1e-12) {
    for (const [y, ny] of [[center.y, -1], [center.y + h, 1]] as const) {
      const t = (y - origin.y) / dir.y;
      const x = ox + dir.x * t;
      const z = oz + dir.z * t;
      if (t >= 0 && t <= maxDist && x * x + z * z <= r * r && (!best || t < best.t)) best = { t, normal: { x: 0, y: ny, z: 0 } };
    }
  }
  return best;
}

/**
 * Nearest construct hit by a ray (or, with `grow`, by a sphere of that radius swept along it);
 * hits exactly at `maxDist` count, so a ray stopped by a solid construct's obstacle still finds it.
 */
export function rayConstructs(sim: Sim, origin: Vec3, dir: Vec3, maxDist: number, filter: (e: number) => boolean, grow = 0): ConstructHit | null {
  let best: ConstructHit | null = null;
  let bestT = maxDist;
  for (const e of sim.constructEids()) {
    if (!filter(e)) continue;
    const kind = CONSTRUCTS[sim.c.Construct.kind[e]!];
    if (!kind) continue;
    const p = sim.posOf(e);
    const hit: { t: number; normal: Vec3 } | null =
      kind.shape.kind === "box"
        ? rayBox(origin, dir, p, sim.c.Construct.yaw[e]!, kind.shape.hw, kind.shape.hd, kind.shape.h, bestT, grow)
        : rayCyl(origin, dir, p, kind.shape.r, kind.shape.h, bestT, grow);
    if (hit && hit.t <= bestT) {
      bestT = hit.t;
      best = { t: hit.t, eid: e, pos: add(origin, scale(dir, hit.t)), normal: hit.normal };
    }
  }
  return best;
}

export function constructObstacles(sim: Sim, filter: (e: number) => boolean = () => true): Obstacle[] {
  const out: Obstacle[] = [];
  for (const e of [...sim.constructEids()].sort((a, b) => sim.c.Net.id[a]! - sim.c.Net.id[b]!)) {
    if (!filter(e)) continue;
    if (!(sim.c.Construct.flags[e]! & CONSTRUCT_FLAG_SOLID)) continue;
    const kind = CONSTRUCTS[sim.c.Construct.kind[e]!];
    if (!kind) continue;
    if (constructMaterialize(kind, sim.c.Construct.age[e]!) < 1) continue;
    const p = sim.posOf(e);
    const yaw = sim.c.Construct.yaw[e]!;
    const fx = -Math.sin(yaw);
    const fz = -Math.cos(yaw);
    if (kind.shape.kind === "box") out.push({ x: p.x, y: p.y, z: p.z, fx, fz, hl: kind.shape.hd, hw: kind.shape.hw, h: kind.shape.h });
    else out.push({ x: p.x, y: p.y, z: p.z, fx, fz, hl: kind.shape.r, hw: kind.shape.r, h: kind.shape.h });
  }
  return out;
}

export function constructObstaclesFromWorld(world: WorldRecords): Obstacle[] {
  const out: Obstacle[] = [];
  for (const [, rec] of [...world].sort((a, b) => a[0] - b[0])) {
    if (rec.type !== EntityType.Construct) continue;
    const f = decodeRecord(rec);
    if (!((f.flags ?? 0) & CONSTRUCT_FLAG_SOLID)) continue;
    const kind = CONSTRUCTS[f.kind!];
    if (!kind) continue;
    if (constructMaterialize(kind, f.age ?? 0) < 1) continue;
    const x = f32(f.x!);
    const y = f32(f.y!);
    const z = f32(f.z!);
    const yaw = f32(f.yaw!);
    const fx = -Math.sin(yaw);
    const fz = -Math.cos(yaw);
    if (kind.shape.kind === "box") out.push({ x, y, z, fx, fz, hl: kind.shape.hd, hw: kind.shape.hw, h: kind.shape.h });
    else out.push({ x, y, z, fx, fz, hl: kind.shape.r, hw: kind.shape.r, h: kind.shape.h });
  }
  return out;
}

export function constructObstaclesFromDecoded(decoded: Map<number, Record<string, number>>, ageOffsetTicks = 0): Obstacle[] {
  const out: Obstacle[] = [];
  for (const [, f] of [...decoded].sort((a, b) => a[0] - b[0])) {
    if (f.kind === undefined || f.owner === undefined || f.flags === undefined || !((f.flags ?? 0) & CONSTRUCT_FLAG_SOLID)) continue;
    const kind = CONSTRUCTS[f.kind];
    if (!kind) continue;
    if (constructMaterialize(kind, (f.age ?? 0) + ageOffsetTicks) < 1) continue;
    const x = f32(f.x!);
    const y = f32(f.y!);
    const z = f32(f.z!);
    const yaw = f32(f.yaw!);
    const fx = -Math.sin(yaw);
    const fz = -Math.cos(yaw);
    if (kind.shape.kind === "box") out.push({ x, y, z, fx, fz, hl: kind.shape.hd, hw: kind.shape.hw, h: kind.shape.h });
    else out.push({ x, y, z, fx, fz, hl: kind.shape.r, hw: kind.shape.r, h: kind.shape.h });
  }
  return out;
}

/** Ticks constructs, expiry and owner-death policy. */
export function stepConstructs(sim: Sim) {
  // oxlint-disable-next-line unicorn/no-useless-spread
  for (const e of [...sim.constructEids()]) {
    const kind = CONSTRUCTS[sim.c.Construct.kind[e]!];
    if (!kind) {
      destroyConstruct(sim, e, "manual");
      continue;
    }
    const owner = sim.c.Construct.owner[e]!;
    if (!(sim.c.Construct.flags[e]! & CONSTRUCT_FLAG_PERSIST) && (!sim.players.has(owner) || !sim.c.Player.alive[owner])) {
      destroyConstruct(sim, e, "owner");
      continue;
    }
    kind.tick?.(sim, e);
    sim.c.Construct.age[e] = sim.c.Construct.age[e]! + 1;
    sim.c.Construct.ticks[e] = sim.c.Construct.ticks[e]! - 1;
    if (sim.c.Construct.ticks[e]! <= 0) destroyConstruct(sim, e, "expired");
  }
  sim.refreshObstacles();
}

/** Credits non-damage summon support as an assist enabler. */
export function markConstructSupport(sim: Sim, source: number, ally: number, ticks = ASSIST_WINDOW) {
  const m = sim.players.get(ally);
  if (!m || sim.c.Player.team[source] !== sim.c.Player.team[ally]) return;
  m.supportedBy.set(source, sim.tick + ticks);
}

export function constructTeam(sim: Sim, e: number): Team {
  return sim.c.Construct.team[e] as Team;
}

/** Construct damage counts as damage dealt for ult energy (at half weight, like barriers). */
function addConstructUlt(sim: Sim, eid: number | undefined, points: number) {
  if (eid === undefined || !sim.authority || !sim.players.has(eid) || sim.c.Ult.active[eid]! > 0) return;
  const def = sim.hero(eid);
  const rate = def.ultGain?.damage ?? ULT_GAIN[def.role].damage;
  sim.c.Ult.charge[eid] = Math.min(1, sim.c.Ult.charge[eid]! + (points * rate) / def.ultCost);
}
